import axios, { AxiosInstance } from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = localStorage.getItem("token");

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (this.token) {
      this.client.defaults.headers.common["Authorization"] =
        `Bearer ${this.token}`;
    }

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem("token", token);
    this.client.defaults.headers.common["Authorization"] =
      `Bearer ${token}`;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("token");
    delete this.client.defaults.headers.common["Authorization"];
  }

  async login(email: string, password: string) {
    const response = await this.client.post("/auth/login", {
      email,
      password,
    });
    return response.data;
  }

  async getMe() {
    const response = await this.client.get("/auth/me");
    return response.data;
  }

  async logout() {
    await this.client.post("/auth/logout");
    this.clearToken();
  }

  async getItems(all = false) {
    const response = await this.client.get("/items", {
      params: all ? { all: "true" } : {},
    });
    return response.data;
  }

  async createItem(data: Record<string, any>) {
    const response = await this.client.post("/items", data);
    return response.data;
  }

  async updateItem(itemId: string, data: Record<string, any>) {
    const response = await this.client.patch(`/items/${itemId}`, data);
    return response.data;
  }

  async getStores() {
    const response = await this.client.get("/stores");
    return response.data;
  }

  async createStore(data: { code: string; name: string }) {
    const response = await this.client.post("/stores", data);
    return response.data;
  }

  async getUsers() {
    const response = await this.client.get("/stores/users");
    return response.data;
  }

  async createUser(data: {
    email: string;
    password: string;
    fullName?: string;
    role: string;
    storeId?: string;
  }) {
    const response = await this.client.post("/stores/users", data);
    return response.data;
  }

  async getLockedPeriods() {
    const response = await this.client.get("/admin/periods");
    return response.data;
  }

  async openDay(storeId: string, date: string) {
    const response = await this.client.post(`/entries/days/${storeId}/open`, {
      date,
    });
    return response.data;
  }

  async getDayEntries(storeId: string, date: string) {
    const response = await this.client.get(`/entries/days/${storeId}`, {
      params: { date },
    });
    return response.data;
  }

  async updateEntry(entryId: string, data: Record<string, any>) {
    const response = await this.client.patch(`/entries/entries/${entryId}`, data);
    return response.data;
  }

  async getMonthEnd(storeId: string, month: string) {
    const response = await this.client.get("/entries/month-end", {
      params: { storeId, month },
    });
    return response.data;
  }

  async getAdminOverview() {
    const response = await this.client.get("/admin/overview");
    return response.data;
  }

  async lockPeriod(storeId: string, month: string) {
    const response = await this.client.post("/admin/periods/lock", {
      storeId,
      month,
    });
    return response.data;
  }

  async unlockPeriod(storeId: string, month: string) {
    const response = await this.client.delete("/admin/periods/lock", {
      data: { storeId, month },
    });
    return response.data;
  }

  async getAudit(params: {
    storeId?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) {
    const response = await this.client.get("/admin/audit", { params });
    return response.data;
  }

  async getTrendsDaily(days = 30) {
    const response = await this.client.get("/admin/trends/daily", {
      params: { days },
    });
    return response.data;
  }

  async getTrendsMonthly(months = 6) {
    const response = await this.client.get("/admin/trends/monthly", {
      params: { months },
    });
    return response.data;
  }

  async downloadMonthExcel(storeId: string, month: string, filename: string) {
    const response = await this.client.get("/entries/month-export", {
      params: { storeId, month },
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}

export const apiClient = new ApiClient();
